import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import cartService from '../services/cartService';
import { useAuth } from './AuthContext';

const CartContext = createContext();

const CART_STORAGE_KEY = 'bookmyfresh_cart';

const loadCartFromStorage = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (error) {
    console.error('Error loading cart from storage:', error);
  }
  return [];
};

const saveCartToStorage = (items) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.error('Error saving cart to storage:', error);
  }
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};

const MAX_FLASH_ITEMS = 4;

export const CartProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [cartItems, setCartItems] = useState(() => loadCartFromStorage());
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Tracks flash SKUs added this session. Flash items use flashSku as their cart id,
  // so both the ref and all cart lookups key on flashSku (a string like "FD-5-12").
  const flashIdsRef = useRef(
    new Set(loadCartFromStorage().filter(i => i.flashSku).map(i => i.flashSku))
  );

  // Fetch cart from server when user logs in
  useEffect(() => {
    const syncCart = async () => {
      if (isAuthenticated && user) {
        setLoading(true);
        try {
          const localItems = loadCartFromStorage();
          const serverCart = await cartService.getCart();
          if (localItems.length > 0 && (!serverCart.items || serverCart.items.length === 0)) {
            // One-time sync: push guest cart items to server on first login
            await cartService.syncLocalCart(localItems);
            const updatedCart = await cartService.getCart();
            const transformed = transformServerCart(updatedCart);
            flashIdsRef.current = new Set(transformed.filter(i => i.flashSku).map(i => i.flashSku));
            setCartItems(transformed);
          } else if (serverCart.items && serverCart.items.length > 0) {
            const transformed = transformServerCart(serverCart);
            flashIdsRef.current = new Set(transformed.filter(i => i.flashSku).map(i => i.flashSku));
            setCartItems(transformed);
          } else {
            // Server cart is empty — make sure local state reflects that
            setCartItems([]);
          }
          // Always clear localStorage for authenticated users so stale guest-cart
          // data can never be re-synced on the next refresh (which was causing
          // removed items to reappear after a page reload).
          localStorage.removeItem(CART_STORAGE_KEY);
        } catch (error) {
          console.error('Failed to sync cart:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    syncCart();
  }, [isAuthenticated, user]);

  const transformServerCart = (serverCart) => {
    if (!serverCart || !serverCart.items) return [];
    return serverCart.items.map(item => {
      const numericProductId = item.product?.id || item.productId;
      const variantId = item.variantId ?? null;
      const variantLabel = item.variantLabel ?? null;
      // Cart key: flash items → flashSku, variant items → "productId_vVariantId", regular → productId
      const cartId = item.flashSku
        ? item.flashSku
        : variantId ? `${numericProductId}_v${variantId}` : numericProductId;
      // Use variant price from product.variants if available, otherwise fall back to product price
      const variant = variantId ? (item.product?.variants ?? []).find(v => v.id === variantId) : null;
      const basePrice = variant ? Number(variant.pricePerUnit) : (item.product?.pricePerUnit ?? item.product?.price_per_unit ?? item.price ?? 0);
      return {
        id: cartId,
        productId: numericProductId,
        cartItemId: item.id,
        name: item.product?.name || item.productName,
        price: item.flashDealPrice ?? basePrice,
        originalPrice: item.flashDealPrice ? basePrice : null,
        flashDealPercent: item.flashDealPercent ?? null,
        flashDealEndsAt: item.flashDealEndsAt ?? null,
        flashSku: item.flashSku || null,
        variantId,
        variantLabel,
        image: item.product?.images?.[0]?.imageUrl || item.product?.image || 'https://via.placeholder.com/200',
        quantity: item.quantity,
        weight: variantLabel || item.product?.unitType || item.product?.unit_type || 'Grams',
      };
    });
  };

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.removeItem(CART_STORAGE_KEY);
    } else if (cartItems.length > 0) {
      saveCartToStorage(cartItems);
    } else {
      // Cart is empty for guest — clear localStorage so stale items don't reappear on reload
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  }, [isAuthenticated, cartItems]);

  // ── Cart mutations ─────────────────────────────────────────────────────────

  const addToCart = useCallback(async (product, variantId = null) => {
    const isFlashItem = product.flashSku != null;

    // ── Flash deal restrictions ────────────────────────────────────────────
    if (isFlashItem) {
      if (flashIdsRef.current.has(product.flashSku)) {
        toast.warning('Only 1 Flash Deal unit can be added per item.', {
          description: 'This product is also available in the regular catalogue.',
        });
        return;
      }
      if (flashIdsRef.current.size >= MAX_FLASH_ITEMS) {
        toast.warning('Flash deal cart limit', {
          description: `You can include up to ${MAX_FLASH_ITEMS} different flash deal items in a single order.`,
        });
        return;
      }
      // Add immediately to prevent concurrent double-clicks
      flashIdsRef.current.add(product.flashSku);
    }

    // Compute the cart key — variant items get their own slot
    const effectiveVariantId = product.variantId ?? variantId ?? null;
    const cartKey = isFlashItem
      ? product.id
      : effectiveVariantId ? `${product.id}_v${effectiveVariantId}` : product.id;

    if (isAuthenticated) {
      try {
        const numericId = product.productId ?? product.id;
        await cartService.addItem(numericId, 1, product.flashSku ?? null, effectiveVariantId);
        const serverCart = await cartService.getCart();
        setCartItems(transformServerCart(serverCart));
        toast.success(`${product.name} added to cart`);
      } catch (error) {
        if (isFlashItem) flashIdsRef.current.delete(product.flashSku);
        console.error('Failed to add to cart:', error);
        toast.error('Failed to add item to cart');
      }
    } else {
      setCartItems((prevItems) => {
        const existingItem = prevItems.find(i => i.id === cartKey);
        if (existingItem) {
          if (isFlashItem) return prevItems;
          return prevItems.map(item =>
            item.id === cartKey
              ? { ...item, quantity: item.quantity + 1 }
              : item,
          );
        }
        return [...prevItems, { ...product, id: cartKey, variantId: effectiveVariantId, quantity: 1 }];
      });
      toast.success(`${product.name} added to cart`);
    }
  }, [isAuthenticated]);

  const removeFromCart = useCallback(async (productId) => {
    flashIdsRef.current.delete(productId);

    if (isAuthenticated) {
      try {
        const item = cartItems.find(i => i.id === productId);
        if (item?.cartItemId) await cartService.removeItem(item.cartItemId);
        const serverCart = await cartService.getCart();
        setCartItems(transformServerCart(serverCart));
        toast.info('Item removed from cart');
      } catch (error) {
        console.error('Failed to remove from cart:', error);
        toast.error('Failed to remove item');
      }
    } else {
      setCartItems(prev => prev.filter(i => i.id !== productId));
      toast.info('Item removed from cart');
    }
  }, [isAuthenticated, cartItems]);

  const updateQuantity = useCallback(async (productId, quantity) => {
    if (quantity < 1) { removeFromCart(productId); return; }

    // Flash deal items are capped at 1 unit
    if (quantity > 1 && flashIdsRef.current.has(productId)) {
      toast.warning('Only 1 Flash Deal unit can be added per item.', {
        description: 'This product is also available in the regular catalogue.',
      });
      return;
    }

    if (isAuthenticated) {
      try {
        const item = cartItems.find(i => i.id === productId);
        if (item?.cartItemId) await cartService.updateItem(item.cartItemId, quantity);
        const serverCart = await cartService.getCart();
        setCartItems(transformServerCart(serverCart));
      } catch (error) {
        console.error('Failed to update quantity:', error);
        toast.error('Failed to update quantity');
      }
    } else {
      setCartItems(prev =>
        prev.map(item => item.id === productId ? { ...item, quantity } : item),
      );
    }
  }, [isAuthenticated, cartItems, removeFromCart]);

  const clearCart = useCallback(async () => {
    flashIdsRef.current.clear();
    if (isAuthenticated) {
      try {
        await cartService.clearCart();
        setCartItems([]);
        toast.success('Cart cleared');
      } catch (error) {
        console.error('Failed to clear cart:', error);
        toast.error('Failed to clear cart');
      }
    } else {
      setCartItems([]);
      localStorage.removeItem(CART_STORAGE_KEY);
      toast.success('Cart cleared');
    }
  }, [isAuthenticated]);

  const toggleCart = useCallback(() => setIsCartOpen(prev => !prev), []);
  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  const cartItemsCount = cartItems.reduce((total, item) => total + item.quantity, 0);
  const cartTotal = cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  // Alias kept for backward compatibility with Checkout.jsx and CartDrawer.jsx
  const paidCartTotal = cartTotal;

  const value = useMemo(() => ({
    cartItems,
    paidCartTotal,
    isCartOpen,
    loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    toggleCart,
    openCart,
    closeCart,
    cartItemsCount,
    cartTotal,
  }), [
    cartItems, paidCartTotal,
    isCartOpen, loading, addToCart, removeFromCart, updateQuantity,
    clearCart, toggleCart, openCart, closeCart, cartItemsCount, cartTotal,
  ]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

CartProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CartContext;